<template>
  <div>
    <h3>Image</h3>
    <div
      id="box-container"
    >
      <div
        id="img-container"
        class="rounded"
        :style="{'aspect-ratio': getAspectRatio()}"
        @mouseover="isHoveringOverImage = true"
        @mouseleave="isHoveringOverImage = false"
      >
        <img
          v-if="!isCroppingImage"
          class="rounded"
          :src="displayImageSrc"
          alt="Display Image"
          width="100%"
          :style="{'aspect-ratio': getAspectRatio()}"
        >
        <vue-cropper
          v-else
          ref="cropper"
          :src="displayImageSrc"
          alt="Display Image"
          :view-mode="2"
          :aspect-ratio="getAspectRatio()"
        />
        <div
          v-show="shouldDisplayImageControls"
          id="img-controls"
          class="m-2"
        >
          <b-button
            ref="display-image-delete-btn"
            variant="danger"
            class="mr-2"
            :class="{ 'btn-loading': isDeleteButtonLoading }"
            @click="deleteImage()"
            @focus="setIsImageControlFocused(true)"
            @blur="setIsImageControlFocused(false)"
            :disabled="!canDeleteImage"
          >
            <i
              class="fal fa-trash-can"
            />
          </b-button>
          <b-button
            ref="display-image-delete-btn"
            variant="info"
            @click="setIsCroppingImage(true)"
            :disabled="!canCropImage"
          >
            <i class="fal fa-crop" />
          </b-button>
        </div>
      </div>
      <section>
        <template
          v-if="!isCroppingImage"
        >
          <b-button
            variant="primary"
            class="w-100"
            @click="openUploadImageModal()"
            :disabled="!canUploadImage"
          >
            Upload Image
            <i class="fal fa-upload" />
          </b-button>
        </template>
        <template
          v-else
        >
          <div
            class="d-flex"
          >
            <b-button
              variant="tertiary"
              class="ml-auto mr-1"
              @click="setIsCroppingImage(false)"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :class="{ 'btn-loading': isSaveButtonLoading }"
              @click="cropImage"
              :disabled="!canCropImage"
            >
              Save
            </b-button>
          </div>
        </template>
      </section>
    </div>
    <upload-image-modal
      :custom-props="UploadImageModalProps"
      @uploadImage="uploadImage(...arguments)"
      @closeUploadImageModal="closeUploadImageModal"
    />
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';

import UploadImageModal from '@/modules/content/components/CreativesPage/Sidebar/UploadImageModal';


export default {
  name: 'DisplayImage',
  components: {
    VueCropper,
    UploadImageModal,
  },
  data() {
    return {
      isHoveringOverImage: false,
      isCroppingImage: false,
      isSaveButtonLoading: false,
      isDeleteButtonLoading: false,
      isImgControlInFocus: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
      'feedId',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEM_DISPLAY_PROPERTIES,
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_READ_ONLY,
      campaignTypes.GET_IS_CAMPAIGN_REVIEW,
    ]),
    selectedCreativeTemplateProperties() {
      return this.GET_CONTENT_ITEM_DISPLAY_PROPERTIES(this.selectedArticleUuid, this.selectedCreativeId);
    },
    displayImageSrc() {
      return this.selectedCreativeTemplateProperties.imageFileName || '/images/image-not-found.png';
    },
    shouldDisplayImageControls() {
      if (!this.selectedCreativeTemplateProperties.hasOwnProperty('imageFileName')) return false;
      return this.isImgControlInFocus
        || (this.isHoveringOverImage
        && !this.isCroppingImage);
    },
    UploadImageModalProps() {
      return {
        title: 'Upload Display Image',
        imageURL: this.selectedCreativeTemplateProperties.imageFileName,
        supportedTypes: ['jpg', 'jpeg', 'png'],
        maxSize: {
          size: '5',
          unit: 'mb',
        },
      };
    },
    canUploadImage() {
      return !this.GET_IS_READ_ONLY()
        && !this.GET_IS_CAMPAIGN_REVIEW;
    },
    canDeleteImage() {
      return !this.GET_IS_READ_ONLY()
        && !this.GET_IS_CAMPAIGN_REVIEW;
    },
    canCropImage() {
      return !this.GET_IS_READ_ONLY()
        && !this.GET_IS_CAMPAIGN_REVIEW;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.UPLOAD_CREATIVE_DISPLAY_IMAGE,
      contentTypes.UPDATE_CONTENT_DISPLAY_COPY,
      contentTypes.DELETE_CREATIVE_DISPLAY_IMAGE,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    async uploadImage(payload) {
      if (payload.type === 'url') {
        const requestPayload = {
          advertisersId: this.selectedAdvertiser.id,
          creativeId: this.selectedCreativeId,
          feedId: this.feedId,
          id: this.selectedArticleUuid,
          body: {
            imageFileName: payload.value,
          },
        };
        await this.UPDATE_CONTENT_DISPLAY_COPY(requestPayload);
      } else if (payload.type === 'file') {
        payload.value.formData.append('body', new Blob([JSON.stringify({ contentItemUuid: this.selectedArticleUuid })], { type: 'application/json' }));
        const requestPayload = {
          advertisersId: this.selectedAdvertiser.id,
          creativeId: this.selectedCreativeId,
          formData: payload.value.formData,
        };
        await this.UPLOAD_CREATIVE_DISPLAY_IMAGE(requestPayload);
      }
      this.closeUploadImageModal();
      this.TRIGGER_CREATIVE_RERENDER();
    },
    setIsCroppingImage(isCropping) {
      this.isCroppingImage = isCropping;
      if (isCropping) this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      else this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
    },
    setIsImageControlFocused(isFocused) {
      this.isImgControlInFocus = isFocused;
      if (isFocused) this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      else this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
    },
    async deleteImage() {
      if (!this.canDeleteImage) return;
      this.isDeleteButtonLoading = true;
      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        creativeId: this.selectedCreativeId,
        contentItemUuid: this.selectedArticleUuid,
      };
      await this.DELETE_CREATIVE_DISPLAY_IMAGE(payload);
      this.TRIGGER_CREATIVE_RERENDER();
      this.isDeleteButtonLoading = false;
    },
    async cropImage() {
      if (!this.canCropImage) return;
      this.isSaveButtonLoading = true;
      const cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL('image/jpeg');
      const block = cropImg.split(';');
      const contentType = block[0].split(':')[1];
      const fileType = contentType.split('/')[1];
      const realData = block[1].split(',')[1];
      const blob = this.b64toBlob(realData, contentType);

      const formData = new FormData();
      formData.append('file', blob, `image.${fileType}`);
      formData.append('body', new Blob([JSON.stringify({ contentItemUuid: this.selectedArticleUuid })], { type: 'application/json' }));

      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        creativeId: this.selectedCreativeId,
        formData,
      };
      await this.UPLOAD_CREATIVE_DISPLAY_IMAGE(payload);
      this.TRIGGER_CREATIVE_RERENDER();
      this.setIsCroppingImage(false);
      this.isSaveButtonLoading = false;
    },
    b64toBlob(b64Data, contentType = '', sliceSize = 512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      return new Blob(byteArrays, { type: contentType });
    },
    getAspectRatio() {
      const iFrameImageContainer = document.getElementById(`preview-iframe-${this.selectedCreativeId}`).contentDocument.getElementById('smartmatch-image');
      if (iFrameImageContainer) {
        const iFrameImageContainerComputedStyle = window.getComputedStyle(iFrameImageContainer);
        const marginY = parseInt(iFrameImageContainerComputedStyle['margin-top'], 10) + parseInt(iFrameImageContainerComputedStyle['margin-bottom'], 10);
        const marginX = parseInt(iFrameImageContainerComputedStyle['margin-left'], 10) + parseInt(iFrameImageContainerComputedStyle['margin-right'], 10);
        const height = parseInt(iFrameImageContainerComputedStyle.height, 10) - marginY;
        const width = parseInt(iFrameImageContainerComputedStyle.width, 10) - marginX;
        return width / height;
      }
      return 16 / 9;
    },
    openUploadImageModal() {
      if (!this.canUploadImage) return;
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      this.$root.$emit('bv::show::modal', 'upload-image-modal');
    },
    closeUploadImageModal() {
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
      this.$root.$emit('bv::hide::modal', 'upload-image-modal');
    },
  },
};
</script>
<style scoped lang="scss">
#box-container {
  height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
#img-container {
  position: relative;
  border-radius: $border-radius;
  img {
    object-fit: cover;
  }
}
#img-controls {
  position: absolute;
  bottom: 0px;
  right: 0px;
}
</style>
